/**
 * Автоперезагрузка при стейл-чанках SPA (Ф2 плана 38).
 *
 * После деплоя старый index.html в открытой вкладке ссылается на хеш-чанки,
 * которых больше нет (rsync --delete). Ленивый импорт роута падает
 * «Importing a module script failed». Свежий index.html (no-store) знает живые
 * чанки — достаточно перезагрузить страницу ОДИН раз.
 *
 * Guard от цикла: если только что перезагружались (< COOLDOWN_MS назад по
 * метке в sessionStorage) — НЕ перезагружаем снова (иначе при реальной, не
 * деплойной, ошибке импорта был бы вечный reload-цикл).
 */
const RELOAD_MARK_KEY = 'stale_chunk_reloaded_at';
const COOLDOWN_MS = 30_000;

/**
 * Решает, можно ли перезагрузиться сейчас, и ставит метку. Чистая логика для
 * теста: принимает «сейчас», хранилище и последнюю метку.
 */
export function shouldReloadOnStaleChunk(now: number, lastReloadAt: number | null): boolean {
  if (lastReloadAt !== null && now - lastReloadAt < COOLDOWN_MS) return false;
  return true;
}

/** Провал динамического импорта? (Vite preloadError + типовые тексты браузеров.) */
export function isChunkLoadError(reason: unknown): boolean {
  const msg =
    reason instanceof Error
      ? reason.message
      : typeof reason === 'string'
        ? reason
        : String((reason as { message?: unknown })?.message ?? '');
  return (
    /Importing a module script failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /'text\/html' is not a valid JavaScript MIME type/i.test(msg)
  );
}

/**
 * Вешает обработчики: vite:preloadError (основной сигнал) + подстраховка на
 * unhandledrejection с текстом чанк-ошибки. При срабатывании — один reload с
 * guard'ом. Идемпотентно не требуется (вызывается один раз из main).
 */
export function installStaleChunkReload(win: Window = window): void {
  const tryReload = (reason: unknown, preventable?: { preventDefault: () => void }): void => {
    if (!isChunkLoadError(reason)) return;
    let last: number | null = null;
    try {
      const raw = win.sessionStorage.getItem(RELOAD_MARK_KEY);
      last = raw ? Number(raw) : null;
    } catch {
      last = null;
    }
    const now = Date.now();
    if (!shouldReloadOnStaleChunk(now, last)) return;
    try {
      win.sessionStorage.setItem(RELOAD_MARK_KEY, String(now));
    } catch {
      /* ignore */
    }
    preventable?.preventDefault();
    win.location.reload();
  };

  win.addEventListener('vite:preloadError', (e) => {
    const err = (e as unknown as { payload?: unknown }).payload;
    tryReload(err, e as unknown as { preventDefault: () => void });
  });
  win.addEventListener('unhandledrejection', (e) => {
    tryReload((e as PromiseRejectionEvent).reason);
  });
}
